import { Box, Paper, Skeleton, Tooltip, Typography } from "@leap4/frontend";
import React from "react";
import type { DashboardKpiDto } from "../../../api/generated/models";
import { DASH, formatMetric, formatSigned, signColor, valueFontSize } from "./format";

interface IProps {
    kpis: DashboardKpiDto[];
    /** Short form under the delta, e.g. "vs prev 4 days". */
    baselineLabel: string | null;
    /** Exact dates, shown on hover — too long to sit in the card without wrapping. */
    baselineTitle: string | null;
    loading: boolean;
}

/** Sign wins wherever a figure can be negative: a red minus is never ambiguous. */
const valueColor = (k: DashboardKpiDto): string => {
    // Unsigned metrics stay neutral and let the delta carry the judgement.
    if (!k.signed) return k.accent === "positive" ? "success.main" : "text.primary";
    return signColor(k.value);
};

/** A falling CPM is an improvement, so direction decides — but the colours stay red and green. */
const deltaColor = (k: DashboardKpiDto): string => {
    if (k.deltaAccent === "negative") return "error.main";
    if (k.deltaAccent === "positive") return "success.main";
    return "text.secondary";
};

/** Profitable Days carries its own "of N" and is not a currency, so it opts out of the formatter. */
const displayValue = (k: DashboardKpiDto): string => {
    if (k.key === "profitableDays") {
        return k.value === null ? DASH : `${k.value}${k.sub ? ` ${k.sub}` : ""}`;
    }
    return formatMetric(k.value, k.unit, true);
};

const KpiRow = ({ kpis, baselineLabel, baselineTitle, loading }: IProps): JSX.Element => {
    if (loading && kpis.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    "& > *": { flex: "1 1 160px", minWidth: 0 },
                }}
            >
                {Array.from({ length: 12 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={82} />
                ))}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                "& > *": { flex: "1 1 160px", minWidth: 0 },
            }}
        >
            {kpis.map((k) => (
                <Paper key={k.key} variant="outlined" sx={{ px: 2, py: 1.5, textAlign: "center" }}>
                    {/* The formula, not just the label — how a number is arrived at should never need asking for. */}
                    <Tooltip
                        placement="top"
                        arrow
                        title={
                            <>
                                <Box sx={{ fontWeight: 600 }}>{k.label}</Box>
                                {k.formula && <Box sx={{ mt: 0.5 }}>{k.formula}</Box>}
                            </>
                        }
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                fontWeight: 500,
                                display: "block",
                                // Wraps rather than truncating; flex stretches the row to match.
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                                minHeight: "2.6em",
                                cursor: "help",
                                textDecoration: "underline dotted",
                                textUnderlineOffset: 3,
                                textDecorationColor: "rgba(148,163,184,0.4)",
                            }}
                        >
                            {k.label}
                        </Typography>
                    </Tooltip>
                    <Typography
                        sx={{
                            mt: 0.5,
                            fontSize: valueFontSize(displayValue(k)),
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                            color: valueColor(k),
                        }}
                    >
                        {displayValue(k)}
                    </Typography>
                    {k.delta !== null && k.delta !== undefined && (
                        <Tooltip title={baselineTitle ?? ""} placement="bottom" arrow>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    display: "block",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                <Box component="span" sx={{ color: deltaColor(k) }}>
                                    {formatSigned(k.delta, k.unit, true)}
                                </Box>
                                {baselineLabel ? ` ${baselineLabel}` : ""}
                            </Typography>
                        </Tooltip>
                    )}
                </Paper>
            ))}
        </Box>
    );
};

export default KpiRow;
